import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BedDouble, Users, LogIn, LogOut, Hotel as HotelIcon, QrCode, ArrowRight } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import useFetch from '../../hooks/useFetch';
import useChartMotion from '../../hooks/useChartMotion';
import { useAuth } from '../../context/AuthContext';
import { useEvents } from '../../context/EventContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Button,
  Loader,
  EmptyState,
  SectionTitle,
  stagger,
  label,
} from '../../components/UI';
import { fmtNumber, pct } from '../../utils/format';

const COLORS = { occupied: '#4ADE80', reserved: '#FBBF24', vacant: '#3A3A45', checked_out: '#60A5FA', maintenance: '#F87171' };

export default function HotelDashboard() {
  const { user } = useAuth();
  const { eventId } = useEvents();
  const hotelId = user?.hotel?._id || user?.hotel;

  const { data, loading } = useFetch(
    hotelId ? `/hotels/${hotelId}/occupancy` : null,
    eventId ? { event: eventId } : {},
    { skip: !hotelId },
  );
  const chartMotion = useChartMotion();

  if (loading) return <Loader full label="Loading your property" />;
  if (!hotelId) return <EmptyState message="No property linked to this account" icon={HotelIcon} />;

  const byStatus = data?.byStatus || {};
  const pie = Object.entries(byStatus).map(([k, v]) => ({ name: label(k), key: k, value: v.rooms }));
  const occupied = byStatus.occupied?.rooms || 0;
  const reserved = byStatus.reserved?.rooms || 0;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <Card padding={24} style={{ marginBottom: 20, overflow: 'hidden' }}>
        <motion.div
          animate={{ opacity: [0.35, 0.62, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: -70, right: -30, width: 260, height: 260,
            background: 'radial-gradient(circle, rgba(239,212,138,0.12), transparent 70%)',
            filter: 'blur(36px)', pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <p style={{ fontSize: 11.5, color: 'var(--gold-500)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
              Property portal
            </p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginTop: 6, fontWeight: 600 }}>
              {user?.hotel?.name || 'Your property'}
            </h2>
            <p style={{ color: 'var(--text-mid)', fontSize: 13, marginTop: 5 }}>
              {occupied} room{occupied === 1 ? '' : 's'} currently occupied · {reserved} awaiting arrival
            </p>
          </div>
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <EventPicker />
            <Link to="/hotel/checkin"><Button icon={QrCode}>Check-in desk</Button></Link>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(205px, 1fr))', gap: 14, marginBottom: 20 }}>
        <Stat label="Rooms Allocated" value={fmtNumber(data?.total)} icon={BedDouble} tone="gold" />
        <Stat label="Guests Expected" value={fmtNumber(data?.guests)} icon={Users} tone="info" />
        <Stat label="Checked In" value={occupied} icon={LogIn} tone="success" progress={pct(occupied, data?.total)} />
        <Stat label="Awaiting Arrival" value={reserved} icon={LogOut} tone="warning" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)', gap: 16 }} className="gl-hotel-grid">
        <Card>
          <SectionTitle title="Room status" />
          {pie.length ? (
            <ResponsiveContainer width="100%" height={252}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="45%"
                  innerRadius={54} outerRadius={88} paddingAngle={3} {...chartMotion}>
                  {pie.map((d) => <Cell key={d.key} fill={COLORS[d.key] || '#C9A54D'} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#16161A', border: '1px solid #24242B', borderRadius: 12, fontSize: 12 }} />
                <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#A8A8B3' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState message="No rooms allocated yet" icon={BedDouble} />}
        </Card>

        <Card>
          <SectionTitle title="By category" subtitle="Allocation and occupancy across your room types" />
          {data?.byCategory?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {data.byCategory.map((c, i) => (
                <motion.div key={c._id || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c._id || 'Unspecified'}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-mid)' }}>
                      <strong style={{ color: 'var(--ok)' }}>{c.occupied}</strong> / {c.rooms} occupied
                    </span>
                  </div>
                  <div style={{ height: 7, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${pct(c.occupied, c.rooms)}%` }}
                      transition={{ duration: 0.9, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                      style={{ height: '100%', borderRadius: 99, background: 'linear-gradient(90deg, var(--gold-500), var(--gold-300))' }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : <EmptyState message="No categories to show" icon={BedDouble} />}

          <Link to="/hotel/rooms" style={{ display: 'block', marginTop: 20 }}>
            <Button variant="subtle" size="sm" style={{ width: '100%' }}>
              Manage all rooms <ArrowRight size={13} />
            </Button>
          </Link>
        </Card>
      </div>

      <style>{`@media (max-width: 980px) { .gl-hotel-grid { grid-template-columns: 1fr !important; } }`}</style>
    </motion.div>
  );
}
